import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Upload, X, Loader2, ArrowUpRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { FadeUp } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICE_OPTIONS = [
  "Landscaping",
  "Lawn Care",
  "Lot Clearing",
  "Gravel Driveway",
  "Grading & Leveling",
  "Demolition",
  "Junk Removal / Haul Off",
  "Bobcat / Skid-Steer Work",
  "Other",
];

const INPUT_CLS =
  "rounded-none border-0 border-b border-[#FFFFFF]/25 bg-transparent focus:border-[#DA7B3C] focus-visible:ring-0 focus:ring-0 h-14 px-0 text-lg text-[#FFFFFF] placeholder:text-[#FFFFFF]/35";

const fileToDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

export default function QuoteForm() {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    address: "",
    service_type: "",
    description: "",
  });
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(false);

  const setField = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const handlePhotos = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const room = 6 - photos.length;
    const accepted = files.slice(0, room);
    if (files.length > room)
      toast.warning("You can upload up to 6 photos total.");
    const next = [];
    for (const f of accepted) {
      if (f.size > 4 * 1024 * 1024) {
        toast.error(`${f.name} is over 4MB and was skipped.`);
        continue;
      }
      const dataUrl = await fileToDataUrl(f);
      next.push({ name: f.name, dataUrl });
    }
    setPhotos((p) => [...p, ...next]);
    e.target.value = "";
  };

  const removePhoto = (idx) =>
    setPhotos((p) => p.filter((_, i) => i !== idx));

  const validate = () => {
    if (!form.name.trim()) return "Please enter your name.";
    if (!form.phone.trim() || form.phone.replace(/\D/g, "").length < 7)
      return "Please enter a valid phone number.";
    if (!form.address.trim()) return "Please enter the job site address.";
    if (!form.service_type) return "Please select a service.";
    if (!form.description.trim())
      return "Please describe what you'd like done.";
    return null;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) {
      toast.error(err);
      return;
    }
    setLoading(true);
    try {
      const payload = {
        ...form,
        email: form.email.trim() || null,
        photos: photos.map((p) => p.dataUrl),
      };
      await axios.post(`${API}/quotes`, payload);
      toast.success("Sent. We'll text or call you shortly.");
      setForm({
        name: "",
        phone: "",
        email: "",
        address: "",
        service_type: "",
        description: "",
      });
      setPhotos([]);
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        "Something went wrong. Please call us at 210-900-6542.";
      toast.error(typeof detail === "string" ? detail : "Submission failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="quote"
      data-testid="quote-section"
      className="py-28 md:py-40 bg-[#0E2A1A] text-[#FFFFFF] relative overflow-hidden"
    >
      <div className="max-w-[1440px] mx-auto px-5 sm:px-8 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          <FadeUp className="lg:col-span-5">
            <div className="chapter-line text-[#DA7B3C] mb-6">
              Ch. 05 · Free Estimate
            </div>
            <h2
              className="font-display text-6xl sm:text-7xl md:text-8xl leading-[0.92] tracking-[-0.03em] mb-8"
              style={{ fontWeight: 400 }}
            >
              Tell us
              <br />
              the job.
              <br />
              <span className="italic text-[#DA7B3C]">{"We'll quote."}</span>
            </h2>
            <p className="text-lg md:text-xl text-[#FFFFFF]/75 leading-relaxed max-w-md mb-10">
              {"Snap a few photos, tell us where. We reply with a fair price — usually within an hour during business hours."}
            </p>
            <ul className="space-y-4">
              {[
                "25+ years experience across San Antonio",
                "Upfront pricing — no hidden fees",
                "Same-day & next-day work available",
                "Licensed, insured, family-owned",
              ].map((t) => (
                <li key={t} className="flex items-start gap-4 text-sm">
                  <span className="mt-2 w-4 h-px bg-[#DA7B3C] shrink-0" />
                  <span className="text-[#FFFFFF]/80">{t}</span>
                </li>
              ))}
            </ul>
          </FadeUp>

          <FadeUp delay={0.1} className="lg:col-span-7">
            <form
              onSubmit={onSubmit}
              data-testid="quote-form"
              className="border border-[#FFFFFF]/15 p-6 md:p-12 space-y-8 bg-[#0E2A1A] rounded-sm"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2">
                <div>
                  <Label
                    htmlFor="q-name"
                    className="font-mono-sm text-[#DA7B3C]"
                  >
                    01 · Full Name
                  </Label>
                  <Input
                    id="q-name"
                    data-testid="quote-input-name"
                    value={form.name}
                    onChange={setField("name")}
                    placeholder="Jane Doe"
                    className={INPUT_CLS}
                    required
                  />
                </div>
                <div>
                  <Label
                    htmlFor="q-phone"
                    className="font-mono-sm text-[#DA7B3C]"
                  >
                    02 · Phone
                  </Label>
                  <Input
                    id="q-phone"
                    data-testid="quote-input-phone"
                    type="tel"
                    value={form.phone}
                    onChange={setField("phone")}
                    placeholder="(210) 555-0123"
                    className={INPUT_CLS}
                    required
                  />
                </div>
                <div>
                  <Label
                    htmlFor="q-email"
                    className="font-mono-sm text-[#DA7B3C]"
                  >
                    03 · Email (optional)
                  </Label>
                  <Input
                    id="q-email"
                    data-testid="quote-input-email"
                    type="email"
                    value={form.email}
                    onChange={setField("email")}
                    placeholder="you@example.com"
                    className={INPUT_CLS}
                  />
                </div>
                <div>
                  <Label
                    htmlFor="q-service"
                    className="font-mono-sm text-[#DA7B3C]"
                  >
                    04 · Service
                  </Label>
                  <Select
                    value={form.service_type}
                    onValueChange={(v) =>
                      setForm((f) => ({ ...f, service_type: v }))
                    }
                  >
                    <SelectTrigger
                      id="q-service"
                      data-testid="quote-select-service"
                      className={`${INPUT_CLS} justify-between`}
                    >
                      <SelectValue placeholder="Select..." />
                    </SelectTrigger>
                    <SelectContent className="rounded-sm border border-[#DA7B3C] bg-[#0E2A1A] text-[#FFFFFF]">
                      {SERVICE_OPTIONS.map((s) => (
                        <SelectItem
                          key={s}
                          value={s}
                          data-testid={`quote-service-option-${s
                            .toLowerCase()
                            .replace(/[^a-z]+/g, "-")}`}
                          className="rounded-none focus:bg-[#DA7B3C] focus:text-[#FFFFFF]"
                        >
                          {s}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="md:col-span-2">
                  <Label
                    htmlFor="q-address"
                    className="font-mono-sm text-[#DA7B3C]"
                  >
                    05 · Job Site Address
                  </Label>
                  <Input
                    id="q-address"
                    data-testid="quote-input-address"
                    value={form.address}
                    onChange={setField("address")}
                    placeholder="123 Alamo St, San Antonio, TX"
                    className={INPUT_CLS}
                    required
                  />
                </div>
                <div className="md:col-span-2">
                  <Label
                    htmlFor="q-desc"
                    className="font-mono-sm text-[#DA7B3C]"
                  >
                    06 · Tell us about the job
                  </Label>
                  <Textarea
                    id="q-desc"
                    data-testid="quote-input-description"
                    value={form.description}
                    onChange={setField("description")}
                    placeholder="e.g. Front yard needs new mulch beds, 3 small palms, and sod."
                    className="rounded-none border-0 border-b border-[#FFFFFF]/25 bg-transparent focus:border-[#DA7B3C] focus-visible:ring-0 focus:ring-0 min-h-[110px] p-0 pt-3 text-lg text-[#FFFFFF] placeholder:text-[#FFFFFF]/35 resize-none"
                    required
                  />
                </div>

                <div className="md:col-span-2 pt-4">
                  <Label className="font-mono-sm text-[#DA7B3C] mb-3 block">
                    07 · Photos (optional, up to 6)
                  </Label>
                  <label
                    htmlFor="q-photos"
                    data-testid="quote-photo-dropzone"
                    className="flex flex-col items-center justify-center border border-dashed border-[#FFFFFF]/25 bg-transparent hover:border-[#DA7B3C] hover:bg-[#DA7B3C]/5 cursor-pointer py-8 px-4 transition-all rounded-sm"
                  >
                    <Upload
                      className="w-5 h-5 mb-3 text-[#DA7B3C]"
                      strokeWidth={1.75}
                    />
                    <span className="text-sm font-medium tracking-wide">
                      Tap to add photos
                    </span>
                    <span className="font-mono-sm text-[#FFFFFF]/45 mt-2">
                      Max 4MB each
                    </span>
                    <input
                      id="q-photos"
                      data-testid="quote-input-photos"
                      type="file"
                      accept="image/*"
                      multiple
                      className="hidden"
                      onChange={handlePhotos}
                    />
                  </label>

                  {photos.length > 0 && (
                    <div
                      data-testid="quote-photo-previews"
                      className="mt-4 grid grid-cols-3 sm:grid-cols-6 gap-3"
                    >
                      {photos.map((p, idx) => (
                        <div
                          key={idx}
                          className="relative border border-[#FFFFFF]/20 aspect-square overflow-hidden group"
                        >
                          <img
                            src={p.dataUrl}
                            alt={p.name}
                            className="w-full h-full object-cover"
                          />
                          <button
                            type="button"
                            data-testid={`quote-photo-remove-${idx}`}
                            onClick={() => removePhoto(idx)}
                            className="absolute top-1 right-1 w-6 h-6 bg-[#111613] text-[#FFFFFF] flex items-center justify-center hover:bg-[#DA7B3C] rounded-full"
                            aria-label="Remove photo"
                          >
                            <X className="w-3 h-3" strokeWidth={2.5} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="pt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6 border-t border-[#FFFFFF]/15">
                <p className="text-xs text-[#FFFFFF]/55 leading-relaxed max-w-md">
                  By submitting, you agree to be contacted about your job. We
                  never share your info.
                </p>
                <motion.button
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                  type="submit"
                  data-testid="quote-submit-button"
                  disabled={loading}
                  className="btn-primary disabled:opacity-60"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Sending
                    </>
                  ) : (
                    <>
                      Send Request
                      <ArrowUpRight className="w-4 h-4" strokeWidth={2} />
                    </>
                  )}
                </motion.button>
              </div>
            </form>
          </FadeUp>
        </div>
      </div>
    </section>
  );
}
